import * as React from 'react';
import Dropzone from 'react-dropzone';
import { Select, DynamicSelectIdentifier } from '@common/components';
import {
  FileViewModel,
  DocumentsRequiringExpiration,
  VendorDocumentColumn,
  VendorDocumentNormalized
} from '@models/Document';
import { VendorViewModel } from '@models/Vendor';
import { Form, Control, Errors } from 'react-redux-form';
import { getDefaultErrorMessages, hasDateValue } from '@common/validators';
import * as moment from 'moment';
import ReactDatePicker from 'react-datepicker';
import { dateParser } from '@common/dateParser';

export interface FileUploadProps {
  readonly fileVMs: FileViewModel[];
  readonly vendorVm: VendorViewModel;
  readonly types?: boolean;
  readonly onFileDrop: (files: File[]) => void;
  readonly isModal: boolean;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly onSaveClick: (fileVMs: FileViewModel[], vendor: VendorViewModel) => void;
  readonly resetForm: () => void;
  readonly forContract: boolean;
}

export interface FileUploadForm {
  fileVMs: FileViewModel[];
  vendorVm: VendorViewModel;
}

export class FileUploadComponent extends React.Component<FileUploadProps> {
  constructor(props: FileUploadProps) {
    super(props);
    this.onDrop = this.onDrop.bind(this);
    this.onSaveClick = this.onSaveClick.bind(this);
  }

  componentWillUnmount() {
    this.props.resetForm();
  }

  onDrop(files: File[]) {
    this.props.onFileDrop(files);
  }

  onSaveClick = (formUpload: FileUploadForm) => {
    const { vendorVm } = this.props;
    var fileVMs = this.props.fileVMs.map(fileVM => ({ ...fileVM }));
    if (formUpload.fileVMs && formUpload.fileVMs.length > 0) {
      formUpload.fileVMs.forEach((element, i) => {
        fileVMs[i].edocsTypeId = element.edocsTypeId;
        fileVMs[i].documentTypeId = element.documentTypeId;
      });
    }
    const updatedVendor = { ...vendorVm, ...formUpload.vendorVm };
    this.props.onSaveClick(fileVMs, updatedVendor);
  };

  renderHideSave() {
    if (this.props.isModal || !this.props.fileVMs) {
      return (
        <div className="col-md-12">
          <div className="row end-xs">
            <button className="btn btn-hidden" type="submit" ref={this.props.buttonRef}>
              Save
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="col-md-12">
        <div className="row end-xs mt-10 mb-10">
          <button className="btn btn-primary" type="button" ref={this.props.buttonRef}>
            Save
          </button>
        </div>
      </div>
    );
  }

  requiresExpirationDate(documentTypeId: number) {
    return DocumentsRequiringExpiration.indexOf(+documentTypeId) !== -1;
  }

  determineVendorExpirationColumn(documentTypeId: number) {
    return VendorDocumentColumn[+documentTypeId];
  }

  determineVendorExpirationNormalized(documentTypeId: number) {
    return VendorDocumentNormalized[+documentTypeId];
  }

  render() {
    const { fileVMs } = this.props;
    return (
      <>
        <Form model="fileUploadForm" hideNativeErrors={true} onSubmit={this.onSaveClick}>
          <div className="row">
            <div className="col-md-4">
              <Dropzone onDrop={this.onDrop}>
                <p className="p-10">Try dropping some files here, or click to select files to upload.</p>
              </Dropzone>
            </div>
            <div className="col-md-8">
              <h3>Dropped files: </h3>
              {fileVMs &&
                fileVMs.length > 0 &&
                fileVMs.map((fileVM, index) => {
                  const vendorExpirationColumn =
                    fileVM.documentTypeId && this.determineVendorExpirationColumn(fileVM.documentTypeId);
                  const requiresExpiration =
                    fileVM.documentTypeId && this.requiresExpirationDate(fileVM.documentTypeId);
                  return (
                    <div className="row middle-xs" key="index">
                      <div className="col-md-6">
                        <span>
                          {fileVMs[index].file.name} - {fileVMs[index].file.size} bytes
                        </span>
                      </div>
                      <div className="col-md-6">
                        {this.props.forContract && (
                          <Select
                            model={`fileUploadForm.fileVMs[${index}].edocsTypeId`}
                            identifier={DynamicSelectIdentifier.EdocTypes}
                          />
                        )}
                        <div className="row">
                          {this.props.types && (
                            <div className="form-control">
                              <label htmlFor={`fileUploadForm.fileVMs[${index}].documentTypeId`}>Document Type</label>
                              <Select
                                model={`fileUploadForm.fileVMs[${index}].documentTypeId`}
                                identifier={DynamicSelectIdentifier.VendorDocumentTypes}
                                filterOut={{
                                  prop: 'value',
                                  values: fileVMs.map(file => {
                                    const normalizedName =
                                      file.documentTypeId &&
                                      this.determineVendorExpirationNormalized(file.documentTypeId);
                                    return normalizedName;
                                  })
                                }}
                              />
                            </div>
                          )}
                          {fileVM.documentTypeId &&
                            requiresExpiration && (
                              <div className="form-control">
                                <label htmlFor={`fileUploadForm.vendorVm[${vendorExpirationColumn}]`}>
                                  Expiration Date
                                </label>
                                <Control
                                  model={`fileUploadForm.vendorVm[${vendorExpirationColumn}]`}
                                  validators={{ required: hasDateValue }}
                                  component={props => {
                                    let val;
                                    if (props.value && !moment.isMoment(props.value)) {
                                      val = dateParser.parse(props.value);
                                    } else {
                                      // already a moment
                                      val = props.value;
                                    }
                                    return (
                                      <ReactDatePicker
                                        {...props}
                                        autoComplete={false}
                                        value={val ? val.format('MM/DD/YYYY') : ''}
                                        selected={val || null}
                                        minDate={moment()}
                                      />
                                    );
                                  }}
                                />
                                <Errors
                                  className="error error-pad"
                                  model={`fileUploadForm.fileVMs[${index}].expirationDate`}
                                  show={{ touched: true, focus: false }}
                                  messages={getDefaultErrorMessages()}
                                />
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
          <div className="row">{this.renderHideSave()}</div>
        </Form>
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/common/components/file-upload/_components/file-upload.component.tsx